import * as React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Box, useTheme, Typography, Skeleton, IconButton, SvgIcon,
  Button,
} from '@mui/material';
import CustomPagination from '../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { CountZonesResponse } from '../../../app/services/api/generated';
import { useNavigate } from 'react-router-dom';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { Pill } from '../../../components/shared/Pill';
import PillList from '../../../components/shared/PillList';
import { SearchSm, Trash01 } from '../../../assets';
import DeleteCountZonesModal from './CountZonesDeleteModal';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { UserContext } from '../../../components/shared/useUser';
import { useContext } from 'react';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';

export default function StockCountZonesTable(props: {
  readonly list: CountZonesResponse[];
  readonly storeID: string;
  readonly loadData: () => void;
  readonly searchTerm: string;
  readonly isLoading: boolean;
  readonly onClearSearch?: () => void;
  readonly itemSearchTerm?: string;
  readonly onClearItemSearch?: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { countZoneDelete } = useNswagClient();
  const [openModal, setOpenModal] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(-1);

  const [isSnackBarOpen, setIsSnackBarOpen] = React.useState(false);
  const [snackBarSeverity, setSnackBarSeverity] = React.useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [snackbarMessage, setSnackbarMessage] = React.useState<string>('');
  const snackBarDuration = 4000;

  const [page, setPage] = React.useState(0);
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = React.useState<string>('locationId');
  const rowsPerPage = 10;
  const { hasPermissionTo } = useContext(UserContext);
  const hasRightToEdit: boolean = hasPermissionTo(['StockLocationWrite']) || hasPermissionTo(['StockCountAdmin']);
  const isAsc = order === 'asc';

  const handleSort = (property: string) => {
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page on sort
  };

  const filteredData = props.list.filter(x =>
    x.name?.toLowerCase().includes(props.searchTerm.toLowerCase()),
  );

  const sortedData = React.useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const valA = a[orderBy as keyof CountZonesResponse];
      const valB = b[orderBy as keyof CountZonesResponse];

      if (typeof valA === 'string' && typeof valB === 'string') {
        return isAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      if (typeof valA === 'number' && typeof valB === 'number') {
        return isAsc ? valA - valB : valB - valA;
      }

      return 0;
    });
  }, [filteredData, order, orderBy]);

  const visibleRows = () => {
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const maxPages = () => {
    return Math.ceil(sortedData.length / rowsPerPage);
  };

  const handleCancelModal = () => {
    setOpenModal(false);
  };

  const deleteButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    if (!hasRightToEdit) {
      return;
    }
    setDeleteId(id);
    setOpenModal(true);
  };

  const handleConfirmModal = () => {
    if (!hasRightToEdit) {
      return;
    }

    countZoneDelete(deleteId)
      .then((result) => {
        setSnackbarMessage(result.message ?? 'Deleted successfully');
        setSnackBarSeverity('success');
        setIsSnackBarOpen(true);
        props.loadData();
        setOpenModal(false);
      })
      .catch((err) => {
        setSnackbarMessage(err ?? 'Error deleting');
        setSnackBarSeverity('error');
        setIsSnackBarOpen(true);
        setOpenModal(false);
      });
  };

  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
    setSnackbarMessage('');
  };

  React.useEffect(() => {
    setPage(0);
  }, [props.searchTerm]);

  if (!props.isLoading && sortedData.length === 0 && props.itemSearchTerm) {
    return (
      <>
        <Box sx={{ p: 5 }}>
          <Typography variant='textMD'
            fontWeight={600}
            sx={{ display: 'flex', alignItems: 'center' }}>
            {t('stockCountZoneWithItems.countZones')}
            <Pill text={`0 ${t('stockCountZoneWithItems.countZones')}`} />
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 12,
          borderTop: '1px solid',
          borderColor: theme.palette.custom.gray[200],
        }}>
          <Box sx={{
            minWidth: '48px',
            minHeight: '48px',
            border: '1px solid',
            borderRadius: '10px',
            borderColor: theme.palette.custom.gray[300],
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 1px 2px 1px #1018280D',
            mb: 4,
          }}>
            <SvgIcon><SearchSm /></SvgIcon>
          </Box>
          <Typography variant='textMD'
            fontWeight={600}>
            {t('itemNotFoundInCountZone')}
          </Typography>
          <Typography variant='textSM'
            color={theme.palette.custom.gray[600]}
            fontWeight={400}
            sx={{ mb: 4 }}>
            {t('itemSearchNoMatch', { searchTerm: props.itemSearchTerm })}
          </Typography>
          <Button
            variant='secondary'
            size='md'
            onClick={props.onClearItemSearch}>
            {t('clearSearch')}
          </Button>
        </Box>
      </>
    );
  }

  if (!props.isLoading && sortedData.length === 0) {
    return (
      <EmptyTable
        text={t('stockCountZoneWithItems.countZones')}
        searchTerm={props.searchTerm}
        onClear={props.onClearSearch ?? props.loadData}
        onAdd={() => { navigate('/create-edit-count-zone'); }}
        onAddIsCreating={false}
        addItemText={t('stockCountZoneWithItems.addCountZone')}
        onAddIsDisabled={!hasRightToEdit}
      />
    );
  }

  return (
    <>
      <Box sx={{ p: 5 }}>
        <Typography variant='textMD'
          fontWeight={600}
          sx={{ display: 'flex', alignItems: 'center' }}>
          {t('stockCountZoneWithItems.countZones')}
          {filteredData.length > 0 && <Pill text={`${filteredData.length} ${t('stockCountZoneWithItems.countZones')}`} />}
        </Typography>
      </Box>
      <MessagesSnackbar
        onClose={closeSnackBar}
        open={isSnackBarOpen}
        severity={snackBarSeverity}
        message={snackbarMessage}
        duration={snackBarDuration}
      />
      {props.isLoading ? (
        <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
          variant='rectangular'
          height={445} />
      ) : (
        <Box sx={{ border: '1px solid', borderColor: theme.palette.custom.gray[200] }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={order}
                      onClick={() => handleSort('name')}
                    >
                      {t('stockCountZoneWithItems.name')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'franchiseName'}
                      direction={order}
                      onClick={() => handleSort('franchiseName')}
                    >
                      {t('stockCountZoneWithItems.franchise')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t('stockCountZoneWithItems.stores')}</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'numberOfItems'}
                      direction={order}
                      onClick={() => handleSort('numberOfItems')}
                    >
                      {t('stockCountZoneWithItems.itemsCount')}
                    </TableSortLabel>
                  </TableCell>
                  {hasRightToEdit && <TableCell>Remove</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows().map((row) => (
                  <TableRow
                    hover
                    key={row.locationId}
                    onClick={() => {
                      if (hasRightToEdit) {
                        navigate(`/create-edit-count-zone/${row.locationId}`);
                      }
                    }}
                    sx={{ cursor: hasRightToEdit ? 'pointer' : 'default' }}
                  >
                    <TableCell>
                      <Typography variant='textXS'
                        fontWeight={600}>{row.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS'>{row.franchiseName}</Typography>
                    </TableCell>
                    <TableCell>
                      <PillList labels={row.storeNames ?? []} />
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS'>{row.numberOfItems}</Typography>
                    </TableCell>
                    {hasRightToEdit && <TableCell
                      align="center"
                      sx={{ p: 0, alignContent: 'center' }}
                    >
                      <IconButton aria-label={t('delete')}
                        size='small'
                        onClick={(event) => deleteButtonHandler(event, row.locationId ?? -1)}>
                        <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01></Trash01></SvgIcon>
                      </IconButton>
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages()}
            breakpointForChangeDisplay={120000}
          />
          {hasRightToEdit && <DeleteCountZonesModal openModal={openModal}
            onConfirm={handleConfirmModal}
            onCancel={handleCancelModal}></DeleteCountZonesModal>}
        </Box>
      )}
    </>
  );
}
